import { useCallback, useRef } from 'react';

// Arrasto com mouse (cursor "grabbing") para listas com overflow-x horizontal.
// Toque nativo já rola sozinho (com momentum) — só interceptamos ponteiros 'mouse'
// para não atrapalhar o scroll por touch no celular.
export default function useDragScroll<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const drag = useRef({
    active: false, startX: 0, scrollLeft: 0, moved: false,
  });

  const onPointerDown = useCallback((event: React.PointerEvent) => {
    const el = ref.current;
    if (!el || event.pointerType !== 'mouse') return;

    drag.current = {
      active: true, startX: event.clientX, scrollLeft: el.scrollLeft, moved: false,
    };
    el.setPointerCapture(event.pointerId);
    el.classList.add('drag-scroll-grabbing');
  }, []);

  const onPointerMove = useCallback((event: React.PointerEvent) => {
    const el = ref.current;
    if (!el || !drag.current.active) return;

    const delta = event.clientX - drag.current.startX;
    if (Math.abs(delta) > 3) drag.current.moved = true;
    el.scrollLeft = drag.current.scrollLeft - delta;
  }, []);

  const endDrag = useCallback((event: React.PointerEvent) => {
    const el = ref.current;
    if (!el || !drag.current.active) return;

    drag.current.active = false;
    el.classList.remove('drag-scroll-grabbing');
    if (el.hasPointerCapture(event.pointerId)) el.releasePointerCapture(event.pointerId);
  }, []);

  const onClickCapture = useCallback((event: React.MouseEvent) => {
    if (drag.current.moved) {
      event.preventDefault();
      event.stopPropagation();
      drag.current.moved = false;
    }
  }, []);

  return {
    ref,
    dragHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerLeave: endDrag,
      onClickCapture,
    },
  };
}
